import { ArrowUpRight, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal, SunMark } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const PROGRAMS = [
  {
    n: "01",
    name: "Clinics",
    tag: "Focused training + personalized coaching",
    tagColor: "aqua",
    copy:
      "Small-group beach volleyball training designed for indoor athletes who want to learn the beach game, get more touches, and receive lots of personalized coaching.",
    facts: ["Summer 2027", "Tuesdays + Thursdays", "June–August", "90-minute sessions"],
    points: [
      "Small groups",
      "No full-season commitment",
      "Choose individual dates",
      "Beach skills, strategy and movement",
    ],
    cta: "Explore Clinics",
    testid: "ways-explore-clinics",
    interest: "clinics",
  },
  {
    n: "02",
    name: "Camps",
    tag: "The fun, easy introduction",
    tagColor: "sun",
    copy:
      "A fun, beginner-friendly way to experience beach volleyball, learn the basics, compete, and spend a few days on the sand.",
    facts: ["Summer 2027", "Ages 10–18", "Multi-day camps"],
    points: [
      "All skills welcome",
      "Learn the basics of beach",
      "Lots of touches",
      "No partner needed",
    ],
    cta: "Explore Camps",
    testid: "ways-explore-camps",
    interest: "camps",
  },
];

export const WaysToPlay = () => {
  const navigate = useNavigate();
  const explore = (interest) => {
    window.dispatchEvent(new CustomEvent("bb:interest", { detail: interest }));
    scrollToId("join");
  };

  return (
    <section id="ways" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-deep/70 flex items-center gap-2">
                <span className="w-8 h-px bg-sun" /> Clinics + Camps
              </p>
              <h2 className="mt-4 font-display uppercase text-deep leading-[0.95] text-[clamp(2.6rem,6vw,4.5rem)]">
                Ways to play.
              </h2>
            </div>
            <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-deep/60 flex items-center gap-2 pb-2">
              <SunMark className="w-5 h-4 text-sun" />
              Summer 2027 registration details coming soon.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 gap-6 md:gap-8">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.n} delay={0.12 * i} className="h-full">
              <article
                data-testid={`ways-card-${p.name.toLowerCase()}`}
                className="group h-full flex flex-col bg-white border border-deep/10 rounded-2xl p-7 md:p-10 shadow-[0_16px_40px_-28px_rgba(11,49,68,0.35)] hover:border-deep/25 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-start justify-between">
                  <p className="font-mono text-xs tracking-[0.2em] text-deep/50">{p.n}</p>
                  <SunMark className="w-6 h-5 text-sun/60 group-hover:text-sun transition-colors duration-300" />
                </div>
                <h3 className="mt-6 font-display uppercase text-deep text-4xl md:text-5xl">{p.name}</h3>
                <span
                  className={`mt-3 self-start font-mono text-[9px] uppercase tracking-[0.18em] rounded-full px-3.5 py-1.5 border ${
                    p.tagColor === "aqua"
                      ? "bg-aqua/15 border-aqua/50 text-deep"
                      : "bg-sun/20 border-sun/70 text-deep"
                  }`}
                >
                  {p.tag}
                </span>
                <p className="mt-4 text-sm md:text-base text-deep-soft leading-relaxed">{p.copy}</p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {p.facts.map((f) => (
                    <span
                      key={f}
                      className="font-mono text-[9px] uppercase tracking-[0.14em] text-deep/70 border border-deep/15 rounded-md px-2.5 py-1.5 bg-tint/60"
                    >
                      {f}
                    </span>
                  ))}
                </div>

                <ul className="mt-6 space-y-2.5">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2.5 text-sm text-deep">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${p.tagColor === "aqua" ? "text-aqua" : "text-sun"}`} />
                      {pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6 border-t border-dashed border-deep/15">
                  <button
                    data-testid={p.testid}
                    onClick={() => explore(p.interest)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-deep text-cream font-mono text-[11px] uppercase tracking-[0.18em] px-6 py-4 rounded-full hover:bg-sun hover:text-deep transition-colors duration-200"
                  >
                    {p.cta}
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <article
            data-testid="ways-card-getaway"
            className="mt-6 md:mt-8 bg-deep text-cream rounded-2xl p-7 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
          >
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-mono text-xs tracking-[0.2em] text-cream/50">03</p>
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] bg-sun text-deep rounded-full px-3 py-1.5">
                  Coming 2028 — for adults
                </span>
              </div>
              <h3 className="mt-4 font-display uppercase text-3xl md:text-4xl">
                The Getaway<span className="text-sun">.</span>
              </h3>
              <p className="mt-1 font-serifit italic text-lg md:text-xl text-cream/90">
                Volleyball. Travel. Community.
              </p>
              <p className="mt-3 text-sm md:text-base text-cream/75 leading-relaxed max-w-xl">
                Beach volleyball trips designed for adults who want to play, explore, and connect
                through the sport.
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] border border-cream/30 rounded-full px-4 py-2">
                Costa Rica — 2028
              </span>
              <button
                data-testid="ways-explore-getaway"
                onClick={() => navigate("/getaway")}
                className="inline-flex items-center gap-2 bg-cream text-deep font-mono text-[11px] uppercase tracking-[0.18em] px-6 py-4 rounded-full hover:bg-sun transition-colors duration-200"
              >
                Explore The Getaway
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </article>
        </Reveal>
      </div>
    </section>
  );
};

export default WaysToPlay;
